import { BadgeVariant } from 'core/elements/badge/Badge'
import Text from 'core/elements/Text'
import calcUsageTotalByPath from 'k8s/util/calcUsageTotals'
import moment from 'moment'
import { getIPAddresses, getRoleStatus, ServiceStatus } from './selector'

export const serviceHealthMap = {
  Healthy: 'success',
  Unhealthy: 'error',
  Connected: 'success',
  Not_Connected: 'error',
  Configured: 'success',
  Not_Configured: 'error',
  'Partially Healthy': 'warning',
  Maintenance: 'warning',
  Warning: 'warning',
  'N/A': 'default',
  Unknown: 'unknown',
  NetworkError: 'warning',
  CertificateError: 'warning',
  'Scheduling Disabled': 'warning',

  // Connectivity status
  ok: 'success',
  unauthorized: 'success',
  converging: 'warning',
  failed: 'error',
  error: 'error',
  offline: 'error',
}

export const healthLableMap = {
  Healthy: 'Healthy',
  Unhealthy: 'Unhealthy',
  Connected: 'Connected',
  Not_Connected: 'Not Connected',
  Configured: 'Configured',
  Not_Configured: 'Not Configured',
  Maintenance: 'Maintenance',
  'N/A': 'Not Configured',
  Unknown: 'Unknown',
  Warning: 'Warning',
  NetworkError: 'NetworkError',
  CertificateError: 'Trust Certificate',

  // Connectivity status
  ok: 'Ok',
  converging: 'Converging',
  failed: 'Failed',
  error: 'Error',
  offline: 'Offline',
  unauthorized: 'Unauthorized',
}

export type ServiceState = { healthy: number; unhealthy: number; warning: number; total: number }

export const formatTime = (timestamp: string | number | Date): string => {
  return moment(timestamp).format('hh:mm:ss A')
}

export const serviceStateCountHelper = (services: any[]) => {
  return services.reduce(
    (accum, service) => {
      if (service?.status === ServiceStatus.Healthy) accum.healthy += 1
      if (service?.status === ServiceStatus.CertificateError) accum.certificateError += 1
      if (service?.status === ServiceStatus.Unhealthy) accum.unhealthy += 1
      if (service?.status === ServiceStatus.Maintenance) accum.maintenance += 1
      if (service?.status === ServiceStatus.SchedulingDisabled) accum.schedulingDisabled += 1
      if (service?.status === ServiceStatus.NetworkError) accum.networkError += 1
      return accum
    },
    {
      healthy: 0,
      unhealthy: 0,
      maintenance: 0,
      schedulingDisabled: 0,
      networkError: 0,
      unauthorized: 0,
      certificateError: 0,
    },
  )
}

export const hostStateCountHelper = (
  hosts: {
    computeServicesStatus?: string
    storageServiceStatus?: string
    networkServiceStatus?: string
    imageServiceStatus?: string
  }[],
) => {
  return hosts.reduce(
    (accum, host) => {
      const relevantStatuses = Object.values(host).filter(
        (status) => typeof status === 'string' && status.toLowerCase() !== 'n/a',
      )

      if (relevantStatuses.length === 0) return accum // Ignore records with only 'N/A'

      const isUnhealthy = relevantStatuses.some((status) => status.toLowerCase() === 'unhealthy')
      const isWarning = relevantStatuses.some((status) =>
        ['networkerror', 'maintenance'].includes(status.toLowerCase()),
      )

      if (isUnhealthy) {
        accum.unhealthy += 1
      } else if (isWarning) {
        accum.warning += 1
      } else {
        accum.healthy += 1
      }

      return accum
    },
    { healthy: 0, warning: 0, unhealthy: 0 },
  )
}

// Aggregate the service states based on the service type
export const aggregateServiceStateOnServiceType = (states: { statusHealth: string }[]) =>
  states?.reduce(
    (acc, curr) => {
      acc.healthy += curr.statusHealth === serviceHealthMap.Healthy ? 1 : 0
      acc.unhealthy += curr.statusHealth === serviceHealthMap.Unhealthy ? 1 : 0
      acc.warning += curr.statusHealth === serviceHealthMap.Warning ? 1 : 0
      acc.total += 1 // Update total count
      return acc
    },
    { healthy: 0, unhealthy: 0, warning: 0, total: 0 },
  )

// Aggregate the states of all services
export const aggregateServiceStates = (states): ServiceState =>
  states.reduce(
    (acc, curr) => {
      acc.healthy += curr.healthy || 0
      acc.unhealthy += curr.unhealthy || 0
      acc.warning += curr.warning || 0
      acc.total = acc.healthy + acc.unhealthy + acc.warning // Update total count
      return acc
    },
    { healthy: 0, unhealthy: 0, warning: 0, total: 0 },
  )

export const mergeHosts = (arrays) => {
  const mergedMap = new Map()

  arrays.flat().forEach((obj) => {
    const hostKey = obj.hostDetails?.id

    if (!mergedMap.has(hostKey)) {
      mergedMap.set(hostKey, {
        ...obj,
        computeStatus: obj.computeServicesStatus || 'N/A',
        storageStatus: obj.storageServiceStatus || 'N/A',
        networkStatus: obj.networkServiceStatus || 'N/A',
        imageStatus: obj.imageServiceStatus || 'N/A',
      })
    } else {
      const existing = mergedMap.get(hostKey)

      // Merge objects while ensuring unique fields are retained
      Object.keys(obj).forEach((key) => {
        if (!existing[key]) {
          existing[key] = obj[key] // Keep new fields
        } else if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
          // For nested objects, keep only one instance (like hostDetails)
          existing[key] = existing[key] || obj[key]
        }
      })

      // Ensure the status fields are updated based on availability
      existing.computeStatus = existing.computeServicesStatus || 'N/A'
      existing.storageStatus = existing.storageServiceStatus || 'N/A'
      existing.networkStatus = existing.networkServiceStatus || 'N/A'
      existing.imageStatus = existing.imageServiceStatus || 'N/A'

      mergedMap.set(hostKey, existing)
    }
  })

  return Array.from(mergedMap.values())
}

export const calculateNodeUsages = (nodes) => {
  const calcNodesTotals = calcUsageTotalByPath(nodes)
  return {
    compute: calcNodesTotals('usage.compute.current', 'usage.compute.max'),
    memory: calcNodesTotals('usage.memory.current', 'usage.memory.max'),
    disk: calcNodesTotals('usage.disk.current', 'usage.disk.max'),
  }
}

export const IPAddressCellComponent = ({ value }) => {
  return (
    <div>
      {value?.map((iface) => (
        <Text key={iface?.ip} variant="body2">
          {iface?.ip} ({iface?.name})
        </Text>
      ))}
    </div>
  )
}

export const getHealthStatus = (phase: string) => {
  const status: BadgeVariant = serviceHealthMap[phase] || 'unknown'
  const label = healthLableMap[phase] || phase
  return { variant: status, label }
}

export const getConnectivityStatus = (phase: string, tooltipBody?: string) => {
  const status: BadgeVariant = serviceHealthMap[phase] || 'error'
  const label = healthLableMap[phase] || phase
  return tooltipBody ? { variant: status, label, tooltipBody } : { variant: status, label }
}

// Get the aggregated Service State based on individual service state
export const getOverallServiceStatus = (
  serviceStates,
  isComputeService?: boolean,
  serviceDetails?: any[],
) => {
  let statusHealth, statusName

  if (isComputeService) {
    // Doc - https://platform9.atlassian.net/wiki/spaces/PM/pages/4757389316/PCD+Service+Health+Dashboard+and+Host+Health+Reporting#Compute-service-health
    // If any compute service is unhealthy, return unhealthy immediately
    if (serviceStates?.unhealthy > 0) {
      statusHealth = serviceHealthMap.Unhealthy
      statusName = healthLableMap.Unhealthy

      return { statusHealth, statusName }
    }

    // For compute services, we also need to consider offline hosts
    // i.e connectivity status of hosts is offline OR if the host is in maintainance
    // If the number of hosts in offline state >= 50%, compute health service  = unhealthy
    // If 25% < hosts in offline state < 50%, compute health service = warning
    // If hosts in offline state <= 25%, compute health service = healthy
    // If there are no online healthy hosts, unhealthy
    const offlineHosts =
      serviceDetails?.filter((service: any) => service?.connectivityStatus === 'offline')?.length ??
      0
    const healthyHosts =
      serviceDetails?.filter(
        (service: any) => service?.computeServicesStatus === ServiceStatus.Healthy,
      ) || []
    const totalHosts = serviceDetails?.length ?? 0
    const offlineHostPercent = totalHosts > 0 ? (offlineHosts / totalHosts) * 100 : 0

    if (!healthyHosts?.length) {
      statusHealth = serviceHealthMap.Unhealthy
      statusName = healthLableMap.Unhealthy
    } else if (offlineHostPercent >= 50) {
      statusHealth = serviceHealthMap.Unhealthy
      statusName = healthLableMap.Unhealthy
    } else if (offlineHostPercent > 25 && offlineHostPercent < 50) {
      statusHealth = serviceHealthMap.Warning
      statusName = healthLableMap.Warning
    } else {
      statusHealth = serviceHealthMap.Healthy
      statusName = healthLableMap.Healthy
    }
    return { statusHealth, statusName }
  }

  statusHealth =
    serviceStates?.unhealthy > 0
      ? serviceHealthMap.Unhealthy
      : serviceStates?.networkError > 0
      ? serviceHealthMap.NetworkError
      : serviceStates?.certificateError > 0
      ? serviceHealthMap.CertificateError
      : serviceStates?.healthy > 0
      ? serviceHealthMap.Healthy
      : serviceHealthMap.Unknown
  statusName =
    serviceStates?.unhealthy > 0
      ? healthLableMap.Unhealthy
      : serviceStates?.networkError > 0
      ? healthLableMap.NetworkError
      : serviceStates?.certificateError > 0
      ? healthLableMap.CertificateError
      : serviceStates?.healthy > 0
      ? healthLableMap.Healthy
      : healthLableMap.Unknown

  return { statusHealth, statusName }
}

// Filter out services that do not have host details
export const validServiceDetails = (services: any[]) => {
  return services.filter((service) => !!service?.hostDetails)
}

export const getMergedStorageServiceDetails = (storageServiceDetails) => {
  const reducedData = storageServiceDetails.reduce(
    (accum, service) => {
      if (accum.statusesByHostId?.[service.hostDetails?.id]) {
        return {
          ...accum,
          statusesByHostId: {
            ...accum.statusesByHostId,
            [service.hostDetails.id]: [
              ...accum.statusesByHostId[service.hostDetails.id],
              { volumeBackend: service.volumeBackend, status: service.status },
            ],
          },
        }
      } else {
        return {
          firstServiceOfHost: [...accum.firstServiceOfHost, service],
          statusesByHostId: {
            ...accum.statusesByHostId,
            [service.hostDetails?.id]: [
              {
                volumeBackend: service.volumeBackend,
                status: service.status,
              },
            ],
          },
        }
      }
    },
    {
      firstServiceOfHost: [],
      statusesByHostId: {},
    },
  )
  const { firstServiceOfHost, statusesByHostId } = reducedData
  return firstServiceOfHost.map((service) => {
    return {
      ...service,
      volumeBackendStatuses: statusesByHostId[service.hostDetails?.id],
    }
  })
}

export const buildCompleteCombinedServices = (
  validCombinedServicesDetails: any[],
  hosts: any[],
): any[] => {
  const usedHostIds = new Set(validCombinedServicesDetails?.map((item) => item?.host))

  const missingHosts = hosts
    ?.filter(
      (host) =>
        !usedHostIds.has(host?.id) && Array.isArray(host?.roles) && host?.roles?.length === 0,
    )
    ?.map((host) => ({
      name: host?.info?.hostname,
      clusterName: host?.clusterName || 'N/A',
      host: host?.id,
      connectivityStatus: host?.info?.responding ? 'ok' : 'unauthorized',
      computeStatus: 'N/A',
      imageStatus: 'N/A',
      storageStatus: 'N/A',
      networkStatus: 'N/A',
      ipAddresses: getIPAddresses(host),
      roleStatus: getRoleStatus(host),
      ...host,
    }))

  return [...validCombinedServicesDetails, ...missingHosts]
}
